import React, { useEffect } from "react";
import { Button, Spin, Tag, Divider, message } from "antd";
import {
  ArrowLeft,
  Package,
  Calendar,
  CreditCard,
  MapPin,
  Phone,
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  User,
} from "lucide-react";
import { API_METHODS } from "../../constants";
import useApi from "../../hooks/useApi";
import HeaderComponent from "./Home/HeaderComponent";
import Footer from "./Home/Footer";
import { useNavigate, useParams, Link } from "react-router-dom";

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); }
    catch { return null; }
  })();

  const [orderResponse, orderRequest] = useApi(
    `/user/orders/${orderId}`,
    null,
    API_METHODS.GET
  );

  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      navigate("/login", { replace: true });
      return;
    }
    orderRequest(null, API_METHODS.GET, `/user/orders/${orderId}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(amount);

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "confirmed": return "green";
      case "completed": return "blue";
      case "pending": return "orange";
      case "payment_failed":
      case "cancelled": return "red";
      default: return "gray";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "confirmed":
      case "completed": return <CheckCircle className="h-4 w-4" />;
      case "pending": return <Clock className="h-4 w-4" />;
      case "payment_failed":
      case "cancelled": return <XCircle className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getOrderSteps = (orderStatus) => [
    { title: "Order Placed", status: "finish" },
    { title: "Puja Scheduled", status: orderStatus === "pending" ? "process" : "finish" },
    {
      title: "Puja Performed",
      status: orderStatus === "completed" ? "finish" : orderStatus === "confirmed" ? "process" : "wait",
    },
    { title: "Blessings Delivered", status: orderStatus === "completed" ? "finish" : "wait" },
  ];

  // Loading
  if (orderResponse?.isLoading || !orderResponse) {
    return (
      <>
        <HeaderComponent />
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 flex items-center justify-center">
          <div className="text-center">
            <Spin size="large" />
            <p className="mt-4 text-base lg:text-lg text-gray-600">Loading order details...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Error
  if (orderResponse?.isError || (!orderResponse?.isLoading && !orderResponse?.data)) {
    return (
      <>
        <HeaderComponent />
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 py-8 lg:py-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="bg-white rounded-xl lg:rounded-2xl p-8 lg:p-12 text-center shadow-xl">
              <div className="text-4xl lg:text-6xl mb-4">😔</div>
              <h2 className="text-xl lg:text-2xl font-bold text-gray-800 mb-4">Order Not Found</h2>
              <p className="text-gray-600 mb-6 text-sm lg:text-base">
                We couldn't find the order you're looking for.
              </p>
              <Button
                type="primary"
                size="large"
                onClick={() => navigate("/orders")}
                className="bg-gradient-to-r from-orange-500 to-red-500 border-none h-12"
              >
                Back to Orders
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const orderData = orderResponse?.data;
  const order = orderData?.order;
  const items = orderData?.items || [];

  if (!order) return null;

  return (
    <>
      {contextHolder}
      <HeaderComponent />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 py-8 lg:py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          {/* Header Card */}
          <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 mb-6 lg:mb-8">
            <button
              onClick={() => navigate("/orders")}
              className="flex items-center text-gray-600 hover:text-orange-600 transition-all mb-4 p-2 rounded-lg hover:bg-orange-50"
            >
              <ArrowLeft className="h-4 w-4 lg:h-5 lg:w-5 mr-2" />
              <span className="text-sm lg:text-base font-medium">Back to Orders</span>
            </button>

            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex items-start space-x-3 lg:space-x-4">
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white text-lg lg:text-xl flex-shrink-0">
                  🪔
                </div>
                <div>
                  <h1 className="text-xl lg:text-3xl font-bold text-gray-800">Order Details</h1>
                  <p className="text-sm lg:text-base text-gray-600 mt-1">#{order.order_number}</p>
                </div>
              </div>

              <Tag
                color={getStatusColor(order.order_status)}
                icon={getStatusIcon(order.order_status)}
                className="self-start text-sm lg:text-base px-3 py-1"
              >
                {order.order_status?.toUpperCase()}
              </Tag>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4 lg:space-y-6">
              <div className="bg-white rounded-xl lg:rounded-2xl shadow-xl p-4 lg:p-8">
                <h2 className="text-lg lg:text-2xl font-semibold text-gray-800 mb-4 lg:mb-6 flex items-center">
                  <Package className="h-5 w-5 lg:h-6 lg:w-6 mr-2 lg:mr-3 text-orange-500" />
                  Booked Items ({items.length})
                </h2>

                <div className="space-y-4 lg:space-y-6">
                  {items.map((item, index) => (
                    <div
                      key={item.id || index}
                      className="border border-gray-200 rounded-lg p-4 lg:p-6"
                    >
                      <div className="space-y-3">
                        {/* Name, package badge, price */}
                        <div className="flex items-start justify-between">
                          <div className="flex-1 mr-4">
                            <h3 className="font-semibold text-gray-800 text-base lg:text-lg">
                              {item.name}
                            </h3>
                            {item.booking_kind === "event" && item.package_type && (
                              <Tag color="orange" className="mt-1 capitalize">
                                {item.package_type}
                              </Tag>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="font-bold text-orange-600 text-lg lg:text-xl">
                              {formatCurrency(item.price * item.quantity)}
                            </div>
                            {item.quantity > 1 && (
                              <div className="text-xs lg:text-sm text-gray-500">
                                ₹{item.price} × {item.quantity}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Service/Event date */}
                        {item.service_date && (
                          <div className="bg-green-50 rounded-lg p-3 border-l-4 border-green-500">
                            <div className="text-sm font-medium text-green-900">
                              <Calendar className="h-4 w-4 inline mr-1" />
                              {formatDate(item.service_date)}
                            </div>
                          </div>
                        )}

                        {/* Event link */}
                        {item.booking_kind === "event" && item.event_slug && (
                          <div className="text-xs text-gray-500">
                            <Link
                              to={`/event/${item.event_slug}`}
                              className="text-orange-500 hover:underline"
                            >
                              View event page →
                            </Link>
                          </div>
                        )}

                        {/* Special instructions / notes */}
                        {item.special_instructions && (
                          <div className="bg-yellow-50 rounded-lg p-3 border-l-4 border-yellow-500">
                            <div className="text-sm">
                              <span className="font-medium text-yellow-800">Special Instructions:</span>
                              <p className="text-yellow-700 mt-1 italic">"{item.special_instructions}"</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Order Notes */}
                {order.notes && (
                  <>
                    <Divider />
                    <div className="mt-6">
                      <h3 className="text-base lg:text-lg font-semibold text-gray-800 mb-3 flex items-center">
                        <FileText className="h-4 w-4 lg:h-5 lg:w-5 mr-2 text-orange-500" />
                        Order Instructions
                      </h3>
                      <div className="bg-amber-50 rounded-lg p-4 border-l-4 border-amber-500">
                        <p className="text-sm lg:text-base text-amber-700 italic">"{order.notes}"</p>
                      </div>
                    </div>
                  </>
                )}

                <Divider />

                <div className="flex justify-between items-center text-lg lg:text-xl font-bold">
                  <span>Total Amount</span>
                  <span className="text-orange-600">{formatCurrency(order.total_amount)}</span>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-4 lg:space-y-6">
              {/* Order Status */}
              <div className="bg-white rounded-xl lg:rounded-2xl shadow-xl p-4 lg:p-6">
                <h3 className="text-base lg:text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <CheckCircle className="h-4 w-4 lg:h-5 lg:w-5 mr-2 text-orange-500" />
                  Order Status
                </h3>

                <div className="mb-4">
                  <Tag
                    color={getStatusColor(order.order_status)}
                    icon={getStatusIcon(order.order_status)}
                    className="text-sm px-3 py-1"
                  >
                    {order.order_status?.toUpperCase()}
                  </Tag>
                </div>

                <div className="space-y-2 text-xs lg:text-sm">
                  {getOrderSteps(order.order_status).map((step, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          step.status === "finish"
                            ? "bg-green-500"
                            : step.status === "process"
                            ? "bg-orange-500"
                            : "bg-gray-300"
                        }`}
                      />
                      <span
                        className={
                          step.status === "finish"
                            ? "text-green-600"
                            : step.status === "process"
                            ? "text-orange-600"
                            : "text-gray-500"
                        }
                      >
                        {step.title}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-3 border-t border-gray-100 space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Order Date:</span>
                    <span className="font-medium">{formatDate(order.created_on)}</span>
                  </div>
                  {order.booking_date && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Puja Date:</span>
                      <span className="font-medium">{formatDate(order.booking_date)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer & Payment - Desktop */}
              <div className="hidden lg:block bg-white rounded-xl lg:rounded-2xl shadow-xl p-4 lg:p-6">
                <h3 className="text-base lg:text-xl font-semibold text-gray-800 mb-3 lg:mb-4 flex items-center">
                  <User className="h-4 w-4 lg:h-5 lg:w-5 mr-2 text-orange-500" />
                  Customer Details
                </h3>

                {currentUser && (
                  <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-3 border border-orange-200 mb-4">
                    <div className="flex items-center mb-1">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3 flex-shrink-0 bg-gradient-to-r from-orange-500 to-red-500">
                        🙏
                      </div>
                      <span className="font-semibold text-gray-800 text-sm lg:text-base">
                        Account Holder
                      </span>
                    </div>
                    <div className="text-orange-700 font-medium text-sm lg:text-base">
                      {currentUser.name || "User"}
                    </div>
                    {currentUser.phone && (
                      <div className="text-xs lg:text-sm text-gray-600">{currentUser.phone}</div>
                    )}
                  </div>
                )}

                <Divider className="my-4" />

                <h3 className="text-base lg:text-xl font-semibold text-gray-800 mb-3 flex items-center">
                  <CreditCard className="h-4 w-4 lg:h-5 lg:w-5 mr-2 text-orange-500" />
                  Payment
                </h3>

                <div className="space-y-2 text-sm lg:text-base">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status</span>
                    <span
                      className={`font-medium capitalize ${
                        order.payment_status === "completed" ? "text-green-600" : "text-orange-600"
                      }`}
                    >
                      {order.payment_status || "Pending"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount</span>
                    <span className="font-bold text-orange-600">{formatCurrency(order.total_amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Method</span>
                    <span className="font-medium">Online Payment</span>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  <Button size="large" className="w-full h-10 lg:h-12" onClick={() => navigate("/contact")}>
                    📧 Contact Support
                  </Button>
                  {order.order_status === "completed" && (
                    <Button
                      type="primary"
                      size="large"
                      className="w-full h-10 lg:h-12 bg-gradient-to-r from-orange-500 to-red-500 border-none"
                      onClick={() => navigate("/services")}
                    >
                      🙏 Book Again
                    </Button>
                  )}
                </div>
              </div>

              {/* Temple Info */}
              <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl p-4">
                <h3 className="text-sm lg:text-base font-semibold text-gray-800 mb-2">🛕 Temple Info</h3>
                <div className="space-y-1 text-xs lg:text-sm">
                  <div className="flex items-center">
                    <MapPin className="h-3 w-3 lg:h-4 lg:w-4 mr-2 text-orange-500 flex-shrink-0" />
                    <span>Mahalaxmi Ambabai Temple, Kolhapur</span>
                  </div>
                  <div className="flex items-center">
                    <Phone className="h-3 w-3 lg:h-4 lg:w-4 mr-2 text-orange-500 flex-shrink-0" />
                    <span>+91 9699588995</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Customer & Payment */}
          <div className="lg:hidden mt-6">
            <div className="bg-white rounded-xl shadow-xl p-4">
              <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center">
                <User className="h-4 w-4 mr-2 text-orange-500" />
                Customer & Payment Details
              </h3>

              {currentUser && (
                <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-3 border border-orange-200 mb-4">
                  <div className="flex items-center mb-1">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3 flex-shrink-0 bg-gradient-to-r from-orange-500 to-red-500">
                      🙏
                    </div>
                    <span className="font-semibold text-gray-800 text-sm">Account Holder</span>
                  </div>
                  <div className="text-orange-700 font-medium text-sm">
                    {currentUser.name || "User"}
                  </div>
                  {currentUser.phone && (
                    <div className="text-xs text-gray-600">{currentUser.phone}</div>
                  )}
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Payment Status</span>
                  <span
                    className={`text-sm font-medium capitalize ${
                      order.payment_status === "completed" ? "text-green-600" : "text-orange-600"
                    }`}
                  >
                    {order.payment_status || "Pending"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Paid</span>
                  <span className="font-bold text-orange-600">{formatCurrency(order.total_amount)}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Button size="large" className="w-full h-12" onClick={() => navigate("/contact")}>
                  📧 Contact Support
                </Button>
                {order.order_status === "completed" && (
                  <Button
                    type="primary"
                    size="large"
                    className="w-full h-12 bg-gradient-to-r from-orange-500 to-red-500 border-none"
                    onClick={() => navigate("/services")}
                  >
                    🙏 Book Again
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="mt-6 lg:mt-8 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl p-4 lg:p-6 text-center">
            <h3 className="text-lg lg:text-xl font-semibold mb-2">🙏 Thank You</h3>
            <p className="text-sm lg:text-base mb-4">
              May Mahalaxmi Ambabai bless you with prosperity and happiness.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="large"
                className="bg-white text-orange-600 border-none hover:bg-gray-100 h-10 lg:h-12"
                onClick={() => navigate("/")}
              >
                🏠 Home
              </Button>
              <Button
                size="large"
                className="bg-white text-orange-600 border-none hover:bg-gray-100 h-10 lg:h-12"
                onClick={() => navigate("/orders")}
              >
                📋 All Orders
              </Button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default OrderDetails;
